import { useMemo, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import { useSelector, useDispatch } from 'react-redux';
import AspectRatioIcon from '@mui/icons-material/AspectRatio';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';

import DateUtils from 'utils/DateUtils';
import FormCheckbox from 'core/forms/FormCheckbox';
import TableContainer from 'core/table/TableContainer';
import TanstackReactTable from 'core/table/TanstackReactTable';

import constants from 'app/config/constants';
import Dimensions from 'app/config/Dimensions';
import { permissions } from 'app/data/permissions';
import { HasPermission } from 'hoc/withRole';
import { getFlashPaymentWebhookLogs } from 'app/features/flash-payment-webhook-logs/flashPaymentWebhookLogSaga';
import { getFlashPaymentWebhookLogBriefDetailsInit } from 'app/features/flash-payment-webhook-logs/flashPaymentWebhookLogSlice';
import FlashPaymentWebhookLogsActions from './FlashPaymentWebhookLogsActions';
import FlashPaymentWebhookLogPagination from './FlashPaymentWebhookLogPagination';

const ContentWrapper = styled(Box)({
  maxWidth: '220px',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

export default function FlashPaymentWebhookLogs({ filterByCustomer, customerId }) {
  const dispatch = useDispatch();

  const [columnVisibility, setColumnVisibility] = useState({});

  const logsState = useSelector((state) => state.flashPaymentWebhookLogs.index);

  const { data, params, isLoading } = logsState;

  const columns = useMemo(
    () => [
      {
        id: constants.TABLE_CHECKBOX_SELECT_CHECKBOX_ID,
        header: ({ table }) => (
          <FormCheckbox
            forTableHead={true}
            {...{
              checked: table.getIsAllRowsSelected(),
              indeterminate: table.getIsSomeRowsSelected(),
              onChange: table.getToggleAllRowsSelectedHandler(),
            }}
          />
        ),
        cell: ({ row }) => (
          <div>
            <FormCheckbox
              {...{
                checked: row.getIsSelected(),
                indeterminate: row.getIsSomeSelected(),
                onChange: row.getToggleSelectedHandler(),
              }}
            />
          </div>
        ),
      },
      {
        id: constants.TABLE_SERIAL_NUMBER_COLUMN_ID,
        header: 'SN',
        accessorFn: (row) => row.sn,
      },
      {
        header: 'ID',
        accessorFn: (row) => (row.id ? row.id : 'N/A'),
      },
      {
        header: 'Transaction ID',
        accessorFn: (row) => (row.transactionId ? row.transactionId : 'N/A'),
      },
      {
        header: 'Flash Payment ID',
        accessorFn: (row) => (row.flashPaymentId ? row.flashPaymentId : 'N/A'),
      },
      {
        header: 'Customer ID',
        accessorFn: (row) => (row.customerId ? row.customerId : 'N/A'),
      },
      {
        header: 'Flash Pay User ID',
        accessorFn: (row) => (row.flashPayUserId ? row.flashPayUserId : 'N/A'),
      },
      {
        header: 'External Reference No',
        accessorFn: (row) => (row.externalReferenceNo ? row.externalReferenceNo : 'N/A'),
      },
      {
        header: 'Transaction Status',
        accessorFn: (row) => (row.transactionStatus ? row.transactionStatus : 'N/A'),
      },
      {
        header: 'Amount',
        accessorFn: (row) => (row.amount || row.amount === 0 ? row.amount : 'N/A'),
      },
      {
        header: 'Total Fee',
        accessorFn: (row) => (row.totalFee || row.totalFee === 0 ? row.totalFee : 'N/A'),
      },
      {
        header: 'Currency',
        accessorFn: (row) => (row.currency ? row.currency : 'N/A'),
      },
      {
        header: 'Sender Company Name',
        accessorFn: (row) => (row.senderCompanyName ? row.senderCompanyName : 'N/A'),
      },
      {
        header: 'Sender Bank Name',
        accessorFn: (row) => (row.senderBankName ? row.senderBankName : 'N/A'),
      },
      {
        header: 'Sender Bank Country',
        accessorFn: (row) => (row.senderBankCountry ? row.senderBankCountry : 'N/A'),
      },
      {
        header: 'Sender Account Name',
        accessorFn: (row) => (row.senderAccountName ? row.senderAccountName : 'N/A'),
      },
      {
        header: 'Recipient Account Name',
        accessorFn: (row) => (row.recipientAccountName ? row.recipientAccountName : 'N/A'),
      },
      {
        header: 'Status Message',
        accessorFn: (row) => (row.statusMessage ? row.statusMessage : 'N/A'),
        cell: ({ getValue }) => <ContentWrapper>{getValue()}</ContentWrapper>,
      },
      {
        header: 'Response Message',
        accessorFn: (row) => (row.responseMessage ? row.responseMessage : 'N/A'),
        cell: ({ getValue }) => <ContentWrapper>{getValue()}</ContentWrapper>,
      },
      {
        header: 'Master Account Log',
        accessorFn: (row) => (row.isMasterAccountLog ? 'Yes' : 'No'),
      },
      {
        header: 'Created At',
        accessorFn: (row) => DateUtils.isoToString(row.createdAt),
      },
      {
        header: 'Cleared At',
        accessorFn: (row) => DateUtils.isoToString(row.clearedAt),
      },
      {
        header: 'Created Date',
        accessorFn: (row) => DateUtils.isoToString(row.createdTs),
      },
      {
        header: 'Created By',
        accessorFn: (row) => (row.createdBy ? row.createdBy : 'N/A'),
      },
      {
        header: 'Updated Date',
        accessorFn: (row) => DateUtils.isoToString(row.updatedTs),
      },
      {
        header: 'Updated By',
        accessorFn: (row) => (row.updatedBy ? row.updatedBy : 'N/A'),
      },
      {
        id: constants.TABLE_ACTION_COLUMN_ID,
        header: 'Action',
        accessorFn: (row) => row.id,
        cell: ({ row }) => (
          <Box display="flex" gap={1}>
            <HasPermission permission={permissions.VIEW_FLASH_WEBHOOK_TRANSACTION_LOG}>
              <IconButton
                size={Dimensions.table.iconButton.size}
                onClick={() => {
                  dispatch(
                    getFlashPaymentWebhookLogBriefDetailsInit({
                      showBriefDetailModal: true,
                      data: row.original,
                    }),
                  );
                }}
              >
                <AspectRatioIcon fontSize={Dimensions.table.iconButton.iconSize} />
              </IconButton>
            </HasPermission>
          </Box>
        ),
      },
    ],
    [],
  );

  const table = useReactTable({
    data,
    columns,
    state: {
      columnVisibility,
    },
    onColumnVisibilityChange: setColumnVisibility,
    getCoreRowModel: getCoreRowModel(),
  });

  useEffect(() => {
    dispatch(
      getFlashPaymentWebhookLogs({
        ...params,
        ...(filterByCustomer ? { CustomerId: customerId } : {}),
      }),
    );
  }, [JSON.stringify(params), filterByCustomer, customerId]);

  return (
    <TableContainer>
      <FlashPaymentWebhookLogsActions
        table={table}
        filterByCustomer={filterByCustomer}
        defaultFilterQueries={filterByCustomer ? { CustomerId: customerId } : {}}
      />
      <TanstackReactTable
        table={table}
        isLoading={isLoading}
        data={data}
        columnsCount={
          columns.length - Object.entries(columnVisibility ?? {}).filter(([, v]) => !v).length
        }
      />
      <FlashPaymentWebhookLogPagination />
    </TableContainer>
  );
}

FlashPaymentWebhookLogs.propTypes = {
  filterByCustomer: PropTypes.bool,
  customerId: PropTypes.string,
};

FlashPaymentWebhookLogs.defaultProps = {
  filterByCustomer: false,
  customerId: null,
};
